import type {
  AppNotificationCandidate,
  NotificationDecision,
  NotificationPolicyContext,
} from './model';

const noSound = (
  reason: NotificationDecision['reason'],
): NotificationDecision => ({ channel: 'silent', sound: 'none', reason });

const requestedSound = (
  candidate: AppNotificationCandidate,
  context: NotificationPolicyContext,
): NotificationDecision['sound'] => {
  if (!context.settings.soundsEnabled) return 'none';
  if (candidate.source === 'matrix-call') return 'ring';
  if (
    candidate.source === 'novu' &&
    candidate.workflowCritical &&
    candidate.tags.includes('audible')
  )
    return 'chime';
  return 'none';
};

export const decideNotification = (
  candidate: AppNotificationCandidate,
  context: NotificationPolicyContext,
): NotificationDecision => {
  if (context.isDuplicate) return noSound('duplicate');
  if (
    candidate.source === 'novu' &&
    candidate.severity !== 'medium' &&
    candidate.severity !== 'high'
  )
    return noSound('passive-severity');

  const foreground = context.isVisible && context.hasFocus;
  if (
    candidate.source === 'matrix-message' &&
    foreground &&
    candidate.roomId === context.activeRoomId
  )
    return noSound('active-room');

  const sound = requestedSound(candidate, context);
  if (foreground) return { channel: 'in-app', sound, reason: 'foreground' };
  if (!context.settings.desktopEnabled) return noSound('desktop-disabled');
  if (!context.desktopAvailable) return noSound('desktop-unavailable');
  return { channel: 'desktop', sound, reason: 'background' };
};
